// ConversionHelper.js — the Quantity Calculator's "Conversion helper" (advanced, opened on demand).
//
//   Convert:  a known mass / litres / item size / % / waste allowance / formula with units
//             → this line item's unit, per 1 unit of the composite
//   Geometry: parts missing from (or wrong in) the model — steel members, splices, plates,
//             piles and pads — as volume, coated area, length or count
//
// Every Apply writes ordinary calculator rows whose cells hold the working (Factor "=120/7850",
// Width "=2*0.304+4*0.165-2*0.0061"), so a reviewer can see — and change — how a number was reached.
// The maths is in utils/conversionRecipes.js.

import React, { useMemo, useState } from 'react';
import './ConversionHelper.css';
import {
  unitKind, unitInfo, densityFromWeight, weightTotal, TYPICAL_DENSITIES, MEASURES, fmt, previewTotal,
  massToUnit, litresToVolume, countFromSize, percentage, wasteMultiplier, unitFormula,
  memberRows, spliceRows, plateRows, concreteRows, iSectionProps, designationKgPerM, STEEL_DENSITY,
} from '../utils/conversionRecipes';

const UNIT_CHOICES = [
  { value: 'CubicMeters', label: 'm³' },
  { value: 'SquareMeters', label: 'm²' },
  { value: 'Meters', label: 'm' },
  { value: 'Count', label: 'No.' },
];
const KIND_TO_UNIT = { volume: 'CubicMeters', area: 'SquareMeters', length: 'Meters', count: 'Count' };

const CONVERT = [
  { key: 'mass', label: 'Mass → unit' },
  { key: 'litres', label: 'Litres → m³' },
  { key: 'size', label: 'Count from item size' },
  { key: 'pct', label: 'Percentage' },
  { key: 'waste', label: 'Waste / allowance' },
  { key: 'formula', label: 'Formula with units' },
];
const SHAPES = [
  { key: 'isection', label: 'I-beam / PFC' },
  { key: 'rhs', label: 'RHS / SHS' },
  { key: 'chs', label: 'CHS' },
  { key: 'angle', label: 'Angle' },
  { key: 'splice', label: 'Splice' },
  { key: 'plates', label: 'Plates' },
  { key: 'cylinder', label: 'Pile / round column' },
  { key: 'rect', label: 'Pad / rectangular solid' },
];

function Field({ label, unit, value, onChange, wide, placeholder }) {
  return (
    <label className="ch__field">
      <span className="ch__label">{label}</span>
      <span className="ch__suffix">
        <input
          className={`ch__input ${wide ? 'ch__input--wide' : ''}`}
          type="text"
          inputMode={wide ? 'text' : 'decimal'}
          value={value ?? ''}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
        {unit && <span className="ch__unit">{unit}</span>}
      </span>
    </label>
  );
}

const cellSummary = (r) => ['count', 'length', 'width', 'height', 'factor']
  .filter(f => String(r[f] ?? '').trim() !== '')
  .map(f => `${f[0].toUpperCase()}${f.slice(1)} ${r[f]}`)
  .join(' · ');

function ConversionHelper({ compositeUnit, materialUnit, materialName, materialWeight, onAddRows, onMultiplyRows, onClose }) {
  const knownKind = unitKind(materialUnit);
  const [unitChoice, setUnitChoice] = useState(KIND_TO_UNIT[knownKind] || 'CubicMeters');
  const effUnit = knownKind ? materialUnit : unitChoice;
  const info = unitInfo(effUnit);
  const compositeLabel = unitInfo(compositeUnit)?.label || 'unit';
  const libraryDensity = densityFromWeight(materialWeight, effUnit);

  const [tab, setTab] = useState('convert');
  const [recipe, setRecipe] = useState('mass');
  const [shape, setShape] = useState('isection');
  const [measure, setMeasure] = useState(knownKind || 'volume');
  const [label, setLabel] = useState('');
  const [applied, setApplied] = useState('');

  // Convert inputs
  const [mass, setMass] = useState({ amount: '', massUnit: 'kg', density: libraryDensity ? String(libraryDensity.value) : '' });
  const [litres, setLitres] = useState({ litres: '', coats: '1' });
  const [size, setSize] = useState({ lengthMm: '', heightMm: '', jointMm: '10' });
  const [pct, setPct] = useState('');
  const [waste, setWaste] = useState('5');
  const [formula, setFormula] = useState('');

  // Geometry inputs
  const [member, setMember] = useState({ count: '1', lengthM: '', designation: '' });
  const [dims, setDims] = useState({});
  const [splice, setSplice] = useState({
    flange: { count: '2', l: '', w: '', t: '' },
    web: { count: '2', l: '', w: '', t: '' },
    bolts: { sides: '2', rows: '', cols: '' },
  });
  const [plates, setPlates] = useState([{ count: '1', l: '', w: '', t: '' }]);
  const [solid, setSolid] = useState({ count: '1', diameterMm: '', lengthMm: '', widthMm: '', depthM: '' });
  const [bothFaces, setBothFaces] = useState(true);
  const [spread, setSpread] = useState({ mode: 'per', n: '1', every: '' });

  const set = (setter) => (key) => (v) => { setApplied(''); setter(prev => ({ ...prev, [key]: v })); };
  const setMassF = set(setMass); const setLitresF = set(setLitres); const setSizeF = set(setSize);
  const setMemberF = set(setMember); const setDimsF = set(setDims); const setSolidF = set(setSolid);
  const setSpreadF = set(setSpread);
  const setSpliceF = (part, key) => (v) => { setApplied(''); setSplice(prev => ({ ...prev, [part]: { ...prev[part], [key]: v } })); };
  const setPlateF = (i, key) => (v) => { setApplied(''); setPlates(prev => prev.map((p, j) => (j === i ? { ...p, [key]: v } : p))); };

  const result = useMemo(() => {
    const name = label.trim();
    if (tab === 'convert') {
      switch (recipe) {
        case 'mass': return massToUnit({ ...mass, label: name }, effUnit);
        case 'litres': return litresToVolume({ ...litres, label: name }, effUnit);
        case 'size': return countFromSize({ ...size, label: name });
        case 'pct': return percentage({ pct, label: name });
        case 'waste': return wasteMultiplier({ pct: waste });
        default: return unitFormula({ expression: formula, label: name }, effUnit);
      }
    }
    const common = { measure, name: name || undefined, spread };
    if (['isection', 'rhs', 'chs', 'angle'].includes(shape)) {
      return memberRows({ ...common, shape, dims, count: member.count, lengthM: member.lengthM, name: name || member.designation || undefined });
    }
    if (shape === 'splice') return spliceRows({ ...common, ...splice, bothFaces, name: name || 'Splice' });
    if (shape === 'plates') return plateRows({ ...common, plates, bothFaces });
    return concreteRows({ ...common, shape, ...solid });
  }, [tab, recipe, shape, measure, label, mass, litres, size, pct, waste, formula, member, dims, splice, plates, solid, bothFaces, spread, effUnit]);

  const resultUnit = tab === 'geometry' ? (unitInfo(KIND_TO_UNIT[measure])?.label || '') : (recipe === 'size' ? 'No.' : (recipe === 'pct' || recipe === 'waste') ? '' : info?.label || '');
  const measureMismatch = tab === 'geometry' && knownKind && measure !== knownKind;

  // The weight of what we're about to add, from the line item's Weight value (kg per its unit). Only
  // meaningful when the result is in the material's OWN unit — weighing a surface area by kg/m³ isn't.
  const resultKind = tab === 'geometry' ? measure : (recipe === 'size' ? 'count' : unitKind(effUnit));
  const weightApplies = !!result?.ok && !!result.rows && !!unitKind(effUnit) && resultKind === unitKind(effUnit);
  const libraryHasWeight = !!densityFromWeight(materialWeight, effUnit);
  const weight = weightApplies && libraryHasWeight ? weightTotal(previewTotal(result.rows), materialWeight, effUnit) : null;

  // a typed I-section checked against its designation's kg/m (310UB40.4 → 40.4)
  const sectionCheck = useMemo(() => {
    if (shape !== 'isection') return null;
    const kg = designationKgPerM(member.designation);
    const props = iSectionProps(dims);
    if (!kg || !props) return null;
    const typed = props.area * STEEL_DENSITY;
    const off = Math.abs(typed - kg) / kg;
    return { kg, typed, ok: off <= 0.08 };
  }, [shape, member.designation, dims]);

  const apply = () => {
    if (!result?.ok) return;
    if (tab === 'convert' && recipe === 'waste') {
      onMultiplyRows(result.multiplier, result.words);
      setApplied(`Every row × ${fmt(result.value)} (${result.words}).`);
      return;
    }
    onAddRows(result.rows);
    setApplied(`${result.rows.length} row${result.rows.length === 1 ? '' : 's'} added.`);
  };

  return (
    <div className="ch">
      <div className="ch__head">
        <span className="ch__title">🧮 Conversion helper</span>
        <span className="ch__context">
          Working in <strong>{info?.label || '?'}</strong> of {materialName ? <strong>{materialName}</strong> : 'this material'} per 1 <strong>{compositeLabel}</strong> of the composite
        </span>
        {!knownKind && (
          <label className="ch__suffix">
            <span className="ch__unit">Line item unit</span>
            <select className="ch__select" value={unitChoice} onChange={(e) => { setUnitChoice(e.target.value); setApplied(''); }}>
              {UNIT_CHOICES.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
            </select>
          </label>
        )}
        <button type="button" className="ch__close" onClick={onClose} title="Close the helper">✕</button>
      </div>

      <div className="ch__tabs">
        <button type="button" className={`ch__tab ${tab === 'convert' ? 'ch__tab--active' : ''}`} onClick={() => { setTab('convert'); setApplied(''); }}>Convert</button>
        <button type="button" className={`ch__tab ${tab === 'geometry' ? 'ch__tab--active' : ''}`} onClick={() => { setTab('geometry'); setApplied(''); }}>Geometry</button>
      </div>

      {tab === 'convert' && (
        <>
          <div className="ch__chips">
            {CONVERT.map(c => (
              <button key={c.key} type="button" className={`ch__chip ${recipe === c.key ? 'ch__chip--active' : ''}`}
                onClick={() => { setRecipe(c.key); setApplied(''); }}>{c.label}</button>
            ))}
          </div>

          {recipe === 'mass' && (
            <>
              <p className="ch__hint">
                You know the mass per {compositeLabel} (e.g. 120 kg of rebar per m³ of concrete). It's divided by the
                material's density to give {info?.label || 'its unit'}.
                {libraryDensity ? <> Density from the library: <strong>{fmt(libraryDensity.value)} {libraryDensity.label}</strong>.</> : null}
              </p>
              <div className="ch__grid">
                <Field label="Mass" value={mass.amount} onChange={setMassF('amount')} placeholder="120" />
                <label className="ch__field">
                  <span className="ch__label">Unit</span>
                  <select className="ch__select" value={mass.massUnit} onChange={(e) => setMassF('massUnit')(e.target.value)}>
                    <option value="kg">kg</option>
                    <option value="t">tonnes</option>
                  </select>
                </label>
                <Field label="Density" unit={`kg/${info?.label || 'unit'}`} value={mass.density} onChange={setMassF('density')} placeholder="7850" />
              </div>
              {info?.kind === 'volume' && (
                <div className="ch__chips">
                  {libraryDensity && (
                    <button type="button" className="ch__chip ch__chip--small" onClick={() => setMassF('density')(String(libraryDensity.value))}>
                      Library {fmt(libraryDensity.value)}
                    </button>
                  )}
                  {TYPICAL_DENSITIES.map(d => (
                    <button key={d.label} type="button"
                      className={`ch__chip ch__chip--small ${String(d.value) === String(mass.density) ? 'ch__chip--active' : ''}`}
                      onClick={() => setMassF('density')(String(d.value))}>
                      {d.label} {d.value}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {recipe === 'litres' && (
            <>
              <p className="ch__hint">Coatings, sealants and liquids: litres per {compositeLabel} × coats ÷ 1000 = m³.</p>
              <div className="ch__grid">
                <Field label="Litres" unit={`L/${compositeLabel}`} value={litres.litres} onChange={setLitresF('litres')} placeholder="0.1" />
                <Field label="Coats" value={litres.coats} onChange={setLitresF('coats')} placeholder="2" />
              </div>
            </>
          )}

          {recipe === 'size' && (
            <>
              <p className="ch__hint">Bricks, blocks, tiles, batts or panels per m²: 1 ÷ ((length + joint) × (height + joint)).</p>
              <div className="ch__grid">
                <Field label="Item length" unit="mm" value={size.lengthMm} onChange={setSizeF('lengthMm')} placeholder="230" />
                <Field label="Item height" unit="mm" value={size.heightMm} onChange={setSizeF('heightMm')} placeholder="76" />
                <Field label="Joint" unit="mm" value={size.jointMm} onChange={setSizeF('jointMm')} placeholder="10" />
              </div>
            </>
          )}

          {recipe === 'pct' && (
            <>
              <p className="ch__hint">A share of the composite, e.g. reinforcement at 1.5 % by volume.</p>
              <div className="ch__grid">
                <Field label="Percentage" unit="%" value={pct} onChange={(v) => { setPct(v); setApplied(''); }} placeholder="1.5" />
              </div>
            </>
          )}

          {recipe === 'waste' && (
            <>
              <p className="ch__hint">Multiplies <strong>every row</strong> already in the calculator — add your quantities first.</p>
              <div className="ch__grid">
                <Field label="Allowance" unit="%" value={waste} onChange={(v) => { setWaste(v); setApplied(''); }} placeholder="5" />
              </div>
            </>
          )}

          {recipe === 'formula' && (
            <>
              <p className="ch__hint">
                Any working, with units: <code>120 kg / (7850 kg/m^3)</code>, <code>0.1 L * 2</code>, <code>pi * (300 mm)^2 * 10 m</code>.
                The answer is converted to {info?.label || 'the line item\'s unit'}, and refused if it can't be.
              </p>
              <div className="ch__grid" style={{ width: '100%' }}>
                <label className="ch__field" style={{ flex: 1 }}>
                  <span className="ch__label">Formula</span>
                  <input className="ch__input ch__input--formula" type="text" value={formula}
                    onChange={(e) => { setFormula(e.target.value); setApplied(''); }} placeholder="120 kg / (7850 kg/m^3)" />
                </label>
              </div>
            </>
          )}
        </>
      )}

      {tab === 'geometry' && (
        <>
          <p className="ch__hint">For parts missing from the model or modelled wrongly. Sizes in mm, member lengths in m.</p>
          <div className="ch__chips">
            {SHAPES.map(s => (
              <button key={s.key} type="button" className={`ch__chip ${shape === s.key ? 'ch__chip--active' : ''}`}
                onClick={() => { setShape(s.key); setApplied(''); }}>{s.label}</button>
            ))}
          </div>

          <div className="ch__grid">
            <label className="ch__field">
              <span className="ch__label">Measure</span>
              <select className="ch__select" value={measure} onChange={(e) => { setMeasure(e.target.value); setApplied(''); }}>
                {Object.entries(MEASURES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
            {(measure === 'area' && ['splice', 'plates'].includes(shape)) && (
              <label className="ch__field">
                <span className="ch__label">Faces</span>
                <select className="ch__select" value={bothFaces ? 'both' : 'one'} onChange={(e) => { setBothFaces(e.target.value === 'both'); setApplied(''); }}>
                  <option value="both">Both faces</option>
                  <option value="one">One face</option>
                </select>
              </label>
            )}
          </div>

          {['isection', 'rhs', 'chs', 'angle'].includes(shape) && (
            <div className="ch__grid">
              {shape === 'isection' && (
                <>
                  <Field label="Designation" wide value={member.designation} onChange={setMemberF('designation')} placeholder="310UB40.4 (optional)" />
                  <Field label="Depth d" unit="mm" value={dims.d} onChange={setDimsF('d')} placeholder="304" />
                  <Field label="Flange b" unit="mm" value={dims.bf} onChange={setDimsF('bf')} placeholder="165" />
                  <Field label="Flange t" unit="mm" value={dims.tf} onChange={setDimsF('tf')} placeholder="10.2" />
                  <Field label="Web t" unit="mm" value={dims.tw} onChange={setDimsF('tw')} placeholder="6.1" />
                </>
              )}
              {shape === 'rhs' && (
                <>
                  <Field label="Width b" unit="mm" value={dims.b} onChange={setDimsF('b')} placeholder="100" />
                  <Field label="Depth d" unit="mm" value={dims.d} onChange={setDimsF('d')} placeholder="100" />
                  <Field label="Wall t" unit="mm" value={dims.t} onChange={setDimsF('t')} placeholder="5" />
                </>
              )}
              {shape === 'chs' && (
                <>
                  <Field label="Outside Ø" unit="mm" value={dims.od} onChange={setDimsF('od')} placeholder="168.3" />
                  <Field label="Wall t" unit="mm" value={dims.t} onChange={setDimsF('t')} placeholder="7.1" />
                </>
              )}
              {shape === 'angle' && (
                <>
                  <Field label="Leg a" unit="mm" value={dims.a} onChange={setDimsF('a')} placeholder="75" />
                  <Field label="Leg b" unit="mm" value={dims.b} onChange={setDimsF('b')} placeholder="75" />
                  <Field label="Thickness t" unit="mm" value={dims.t} onChange={setDimsF('t')} placeholder="6" />
                </>
              )}
              <Field label="Number" value={member.count} onChange={setMemberF('count')} placeholder="1" />
              <Field label="Length each" unit="m" value={member.lengthM} onChange={setMemberF('lengthM')} placeholder="6" />
            </div>
          )}
          {sectionCheck && (
            <p className="ch__hint" style={{ color: sectionCheck.ok ? '#047857' : '#b45309' }}>
              {sectionCheck.ok ? '✓' : '⚠️'} These dimensions give {fmt(sectionCheck.typed)} kg/m; {member.designation} is {fmt(sectionCheck.kg)} kg/m
              {sectionCheck.ok ? ' (root radii account for the difference).' : ' — check the dimensions against the section table.'}
            </p>
          )}

          {shape === 'splice' && (
            <div className="ch__plates">
              {measure === 'count' ? (
                <div className="ch__plate-row">
                  <span className="ch__plate-name">Bolts</span>
                  <Field label="Sides" value={splice.bolts.sides} onChange={setSpliceF('bolts', 'sides')} placeholder="2" />
                  <Field label="Rows" value={splice.bolts.rows} onChange={setSpliceF('bolts', 'rows')} placeholder="2" />
                  <Field label="Columns" value={splice.bolts.cols} onChange={setSpliceF('bolts', 'cols')} placeholder="4" />
                </div>
              ) : (
                ['flange', 'web'].map(part => (
                  <div key={part} className="ch__plate-row">
                    <span className="ch__plate-name">{part === 'flange' ? 'Flange plates' : 'Web plates'}</span>
                    <Field label="No." value={splice[part].count} onChange={setSpliceF(part, 'count')} placeholder="2" />
                    <Field label="Length" unit="mm" value={splice[part].l} onChange={setSpliceF(part, 'l')} placeholder={part === 'flange' ? '400' : '300'} />
                    <Field label="Width" unit="mm" value={splice[part].w} onChange={setSpliceF(part, 'w')} placeholder={part === 'flange' ? '165' : '220'} />
                    <Field label="Thick" unit="mm" value={splice[part].t} onChange={setSpliceF(part, 't')} placeholder={part === 'flange' ? '12' : '8'} />
                  </div>
                ))
              )}
            </div>
          )}

          {shape === 'plates' && (
            <div className="ch__plates">
              {plates.map((p, i) => (
                <div key={i} className="ch__plate-row">
                  <span className="ch__plate-name">Plate {i + 1}</span>
                  <Field label="No." value={p.count} onChange={setPlateF(i, 'count')} placeholder="1" />
                  <Field label="Length" unit="mm" value={p.l} onChange={setPlateF(i, 'l')} placeholder="300" />
                  <Field label="Width" unit="mm" value={p.w} onChange={setPlateF(i, 'w')} placeholder="300" />
                  <Field label="Thick" unit="mm" value={p.t} onChange={setPlateF(i, 't')} placeholder="16" />
                  {plates.length > 1 && (
                    <button type="button" className="ch__chip ch__chip--small" onClick={() => setPlates(prev => prev.filter((_, j) => j !== i))}>Remove</button>
                  )}
                </div>
              ))}
              <div>
                <button type="button" className="ch__chip" onClick={() => setPlates(prev => [...prev, { count: '1', l: '', w: '', t: '' }])}>+ Another plate</button>
              </div>
            </div>
          )}

          {(shape === 'cylinder' || shape === 'rect') && (
            <div className="ch__grid">
              <Field label="Number" value={solid.count} onChange={setSolidF('count')} placeholder="1" />
              {shape === 'cylinder' ? (
                <Field label="Diameter" unit="mm" value={solid.diameterMm} onChange={setSolidF('diameterMm')} placeholder="600" />
              ) : (
                <>
                  <Field label="Length" unit="mm" value={solid.lengthMm} onChange={setSolidF('lengthMm')} placeholder="1200" />
                  <Field label="Width" unit="mm" value={solid.widthMm} onChange={setSolidF('widthMm')} placeholder="1200" />
                </>
              )}
              <Field label={shape === 'cylinder' ? 'Length / depth' : 'Depth'} unit="m" value={solid.depthM} onChange={setSolidF('depthM')} placeholder={shape === 'cylinder' ? '10' : '0.6'} />
            </div>
          )}

          <div className="ch__grid">
            <label className="ch__field">
              <span className="ch__label">How often, per {compositeLabel} of composite</span>
              <span className="ch__suffix">
                <select className="ch__select" value={spread.mode} onChange={(e) => setSpreadF('mode')(e.target.value)}>
                  <option value="per">Number per {compositeLabel}</option>
                  <option value="every">1 every …</option>
                </select>
                {spread.mode === 'per' ? (
                  <input className="ch__input" type="text" inputMode="decimal" value={spread.n} onChange={(e) => setSpreadF('n')(e.target.value)} placeholder="1" />
                ) : (
                  <>
                    <input className="ch__input" type="text" inputMode="decimal" value={spread.every} onChange={(e) => setSpreadF('every')(e.target.value)} placeholder="12" />
                    <span className="ch__unit">{compositeLabel}</span>
                  </>
                )}
              </span>
            </label>
          </div>
          {measureMismatch && (
            <p className="ch__hint" style={{ color: '#b45309' }}>
              ⚠️ This line item is measured in {info?.label}, but you're measuring {MEASURES[measure].toLowerCase()}.
            </p>
          )}
        </>
      )}

      {!(tab === 'convert' && recipe === 'waste') && (
        <div className="ch__grid">
          <Field label="Row description (optional)" wide value={label} onChange={(v) => { setLabel(v); setApplied(''); }} placeholder="e.g. Rebar, Splice at mid-span" />
        </div>
      )}

      {result && !result.ok && <div className="ch__error">{result.error}</div>}
      {result?.ok && result.rows && (
        <div className="ch__preview">
          {result.rows.map((r, i) => (
            <div key={i} className="ch__preview-row">
              <span className="ch__preview-desc">{r.description}</span>
              <span className="ch__preview-cells">{cellSummary(r)}</span>
            </div>
          ))}
          <div className="ch__preview-row ch__preview-total">
            <span>Adds</span>
            <span>{fmt(previewTotal(result.rows))} {resultUnit} per 1 {compositeLabel}</span>
          </div>
          {weight && (
            <div className="ch__preview-row ch__preview-weight">
              <span>Weight total</span>
              <span>{weight.display} per 1 {compositeLabel}</span>
            </div>
          )}
          {weightApplies && !libraryHasWeight && (
            <div className="ch__preview-row ch__preview-weight ch__preview-weight--muted">
              <span>Weight total</span>
              <span>add a Weight to this material to see it</span>
            </div>
          )}
        </div>
      )}
      {result?.ok && !result.rows && (
        <div className="ch__preview">
          <div className="ch__preview-row ch__preview-total"><span>Every row</span><span>× {fmt(result.value)}</span></div>
        </div>
      )}

      <div className="ch__actions">
        <button type="button" className="ch__apply" disabled={!result?.ok} onClick={apply}>
          {tab === 'convert' && recipe === 'waste' ? 'Apply to all rows' : 'Add to calculator ↓'}
        </button>
        {applied && <span className="ch__applied">✓ {applied}</span>}
      </div>
    </div>
  );
}

export default ConversionHelper;
